import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useApi } from '../lib/api'
import { Spinner } from '../components/Spinner'
import type { PushCampaign } from '../lib/types'

export function PushHistory() {
  const { call } = useApi()
  const [rows, setRows] = useState<PushCampaign[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    call<PushCampaign[]>('/push-campaigns')
      .then((data) => {
        if (!cancelled) setRows(data)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })
    return () => {
      cancelled = true
    }
  }, [call])

  return (
    <div>
      <header className="mb-8">
        <Link to="/push" className="text-sm text-rust-600 hover:underline">
          ← Compose
        </Link>
        <h1 className="mt-2 font-serif text-3xl font-semibold text-marrow-900 dark:text-bone-50">
          Push history
        </h1>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {rows === null ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-bone-300 bg-bone-100/40 p-12 text-center text-sm text-marrow-800/70 dark:border-marrow-700 dark:bg-marrow-800/40 dark:text-bone-200/70">
          No campaigns yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-bone-200 bg-white dark:border-marrow-700 dark:bg-marrow-800">
          <table className="w-full text-sm">
            <thead className="border-b border-bone-200 bg-bone-100/40 text-xs uppercase tracking-wider text-marrow-800/60 dark:border-marrow-700 dark:bg-marrow-700/40 dark:text-bone-200/60">
              <tr>
                <th className="px-4 py-2 text-left">Title</th>
                <th className="px-4 py-2 text-left">Audience</th>
                <th className="px-4 py-2 text-right">Sent</th>
                <th className="px-4 py-2 text-right">Failed</th>
                <th className="px-4 py-2 text-left">Env</th>
                <th className="px-4 py-2 text-left">When</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-bone-200 last:border-b-0 dark:border-marrow-700"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-marrow-900 dark:text-bone-50">{c.title}</div>
                    {c.deep_link && (
                      <div className="font-mono text-xs text-marrow-800/60 dark:text-bone-200/60">
                        → {c.deep_link}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 capitalize text-marrow-800/80 dark:text-bone-200/80">
                    {c.audience_kind.replace('_', ' ')}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{c.sent_count}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-red-700 dark:text-red-400">
                    {c.failed_count > 0 ? c.failed_count : ''}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        ((c as PushCampaign & { apns_env?: string }).apns_env ?? '') === 'production'
                          ? 'rounded-full bg-rust-500/10 px-2 py-0.5 text-xs font-medium text-rust-700'
                          : 'rounded-full bg-bone-200 px-2 py-0.5 text-xs font-medium text-marrow-800 dark:bg-marrow-700 dark:text-bone-200'
                      }
                    >
                      {(c as PushCampaign & { apns_env?: string }).apns_env ?? '?'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-marrow-800/70 dark:text-bone-200/70">
                    {c.sent_at
                      ? new Date(c.sent_at).toLocaleString(undefined, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })
                      : 'pending'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
